import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useWeekIndex } from "../../../../contexts/WeekIndexProvider";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { useNurseScheduleWeeks } from "../../../../hooks/useNurseScheduleWeeks";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { getVisibleWeekOffset, getWeekStart } from "../../../../utils/dateUtils";
import { nurseShiftsInTimeFrame } from "../../../../utils/departmentWeeks";
import type { Nurse } from "../../../../utils/models";
import { SkeletonWithRandomWidths } from "../util/constants";
import { FirstShiftDialog } from "./FirstShiftDialog";
import { VirtualizedWeekScheduleGrid } from "./VirtualizedWeekScheduleGrid";
import { buildWeekRows } from "./weekScheduleCellResolver";

type Props = {
	nurse: Nurse;
};

export const WeeklySchedule: FC<Props> = ({ nurse }) => {
	const { t } = useTranslation();
	const {
		weeks: scheduleWeeks,
		isLoading: isLoadingSchedule,
		isError: isScheduleError,
		isLoadingMoreWeeks,
		loadEarlierWeeks,
		loadLaterWeeks,
	} = useNurseScheduleWeeks(nurse);
	const { isAnyDemo, isMedicalDepartment, timezone } = useDepartmentFlags();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const { viewStartDay } = useWeekIndex();
	const [showFirstShiftDialog, setShowFirstShiftDialog] = useState(false);

	const offset = isDynamicWeekStartEnabled ? getVisibleWeekOffset(viewStartDay) : 0;

	const toWeekStart = useCallback(
		(date: DateTime): DateTime => (isDynamicWeekStartEnabled ? getWeekStart(date, viewStartDay) : date.startOf("day").startOf("week")),
		[isDynamicWeekStartEnabled, viewStartDay],
	);

	const startOfCurrentWeek = useMemo(() => toWeekStart(DateTime.now().setZone(timezone)), [timezone, toWeekStart]);

	const weeks = useMemo(
		() => (scheduleWeeks ? buildWeekRows(scheduleWeeks, offset, startOfCurrentWeek, isMedicalDepartment, timezone) : []),
		[scheduleWeeks, offset, startOfCurrentWeek, isMedicalDepartment, timezone],
	);

	const handleDemoFirstShiftCreated = useCallback(() => {
		if (!isAnyDemo || !nurse.id || !nurse.createdAt || !scheduleWeeks) return;
		const startOfWeek = toWeekStart(DateTime.now());
		const dateToUse = startOfWeek < nurse.createdAt ? startOfWeek : nurse.createdAt;
		// Counts the weeks as they were before this save's refetch lands, so zero means this was the first shift.
		const shiftsAmountSinceCreated = nurseShiftsInTimeFrame(scheduleWeeks, nurse.id, dateToUse, undefined, false).length;
		if (shiftsAmountSinceCreated === 0) {
			setShowFirstShiftDialog(true);
		}
	}, [isAnyDemo, scheduleWeeks, nurse.id, nurse.createdAt, toWeekStart]);

	const handleCloseDemoDialog = useCallback(() => {
		setShowFirstShiftDialog(false);
	}, []);

	if (isLoadingSchedule) {
		return <SkeletonWithRandomWidths card />;
	}
	// A failed first load leaves no weeks, and so does a failed page fetched while reaching the schedule; a
	// failed later page keeps the weeks already shown.
	if (!scheduleWeeks || (weeks.length === 0 && isScheduleError)) {
		return <EmptyState title={t("error.nurse.schedule.data")} dataTestId="profile-weekly-schedule-error" />;
	}
	if (weeks.length === 0) {
		return <EmptyState title={t("profile.schedule.empty")} dataTestId="profile-weekly-schedule-empty" />;
	}

	return (
		<Box data-testid="profile-weekly-schedule" style={{ width: "100%", position: "relative" }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
			<VirtualizedWeekScheduleGrid
				weeks={weeks}
				weeksData={scheduleWeeks}
				nurseFromProfile={nurse}
				onCreatedSingleShift={handleDemoFirstShiftCreated}
				onReachStart={loadEarlierWeeks}
				onReachEnd={loadLaterWeeks}
				isLoadingMoreWeeks={isLoadingMoreWeeks}
				offset={offset}
				showWeekStartDaySelector={true}
				showExportButton={true}
			/>
			{showFirstShiftDialog && <FirstShiftDialog open={showFirstShiftDialog} onClose={handleCloseDemoDialog} nurse={nurse} />}
		</Box>
	);
};
